import React, {
  RefObject,
  SyntheticEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import FaxRoundedIcon from '@mui/icons-material/FaxRounded';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import AutoCompletedInput from 'core/components/AutoComplete';
import {
  FaxContactDTO,
  PagePropertiesDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  formatPhone,
  isEmptyString,
} from '@populateui/utils';
import Name from 'pages/Dashboard/components/Name';
import values from 'lodash/values';
import clsx from 'clsx';
import DefaultOptionContainer from 'core/components/Lookup/Generic/DefaultOptionContainer';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';

function ContactRow({ name, faxNumber }: FaxContactDTO) {
  return (
    <Name
      name={name ?? ''}
      image
    >
      {formatPhone(faxNumber ?? '')}
    </Name>
  );
}

type ContactLookupProps = {
  value?: FaxContactDTO;
  inputRef?: RefObject<HTMLInputElement>;
  contacts: readonly FaxContactDTO[];
  pageProps: PagePropertiesDTO;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  onChange: (value: FaxContactDTO | FaxContactDTO[] | null) => void;
  isSubmitting: boolean;
  handleInputChange: (event: SyntheticEvent, newInputValue: string) => void;
  error?: boolean;
  handleOpen?: () => void;
  handleClose?: () => void;
  open: boolean;
  isLoading?: boolean;
  isSearching: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isFetching: boolean;
};

export default function ContactLookup({
  value,
  inputRef,
  contacts,
  pageProps,
  searchText,
  setSearchText,
  onChange,
  isSubmitting,
  handleInputChange,
  error,
  handleOpen,
  handleClose,
  open,
  isLoading,
  isSearching,
  page,
  setPage,
  isFetching,
  ...field
}: ContactLookupProps) {
  const [pageChanged, setPageChanged] = useState<boolean>(false);
  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);

  const noOptionsText = useMemo(() => (
    searchText === ''
      ? 'Start typing to see contacts'
      : 'No Contacts Found'
  ), [searchText]);

  const isLoadingMore = useMemo(() => (
    isFetching && pageChanged
  ), [isFetching, pageChanged]);

  const nextPage = useCallback(() => {
    setPageChanged(true);
    setPage(page + 1);
  }, [page, setPage]);

  return (
    <Wrapper
      id="contact"
      showErrorText={false}
      label="Recipient"
      required
      className="flex-1 w-full"
      error={error}
    >
      <AutoCompletedInput<FaxContactDTO>
        {...field}
        id="contact"
        noOptionsText={noOptionsText}
        onChange={onChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        inputRef={inputRef}
        disabled={isSubmitting}
        isOptionEqualToValue={(option, value) => (
          option.faxContactId === (value as FaxContactDTO)?.faxContactId
        )}
        className="w-full"
        clearOnBlur={false}
        openOnFocus
        includeInputInList
        options={contacts}
        getLabel={({ name }) => name ?? ''}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <DefaultOptionContainer
              key={key}
              {...optionProps}
              id={option.faxContactId ?? ''}
            >
              <ContactRow {...option} />
            </DefaultOptionContainer>
          );
        }}
        slotProps={{
          listbox: {
            component: ListboxDropdown,
            optionsLoading: isLoadingMore,
            fetchNextPage: nextPage,
            pageProperties: pageProps,
            isLoading,
            isSearching,
            page,
            setPage,
            isLoadingMore: isFetching,
          },
        } as Object}
        dropdownClassName="!overflow-hidden p-2"
        placeholder="Enter contact name or phone number"
        onInputChange={handleInputChange}
        value={value ?? null}
        blurOnSelect={false}
        filterOptions={(x) => x}
        InputProps={{
          classes: { root: clsx('!pr-2') },
          endAdornment: (
            <div className="flex items-center gap-2">
              {!isEmpty && (
                <>
                  <div className="flex items-center gap-px font-semibold text-gray-500">
                    <FaxRoundedIcon className="!w-4 !h-4 !text-inherited" />
                    {formatPhone(value?.faxNumber ?? '')}
                  </div>
                  {!isSubmitting && (
                    <IconButton
                      onClick={() => {
                        onChange(null);
                        setSearchText('');
                      }}
                      className="!p-0"
                    >
                      <CloseIcon className="!w-4 !h-4" />
                    </IconButton>
                  )}
                </>
              )}
            </div>
          ),
        }}
      />
    </Wrapper>
  );
}
